import { LitElement, html, TemplateResult } from 'lit';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { query, customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { LPWidgetConfig } from '../types';
import { htmlDecode, htmlEncode } from '../../utils/escape.utils';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { getLandingPageReference } from '../../landingPage/utils';

/**
 * @summary Type here the description of this component
 * @tag landingPageTemplate
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-landing-page-template')
export class LandingPageTemplateWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @query('#gridContainer')
  private gridContainer: HTMLElement;

  /**
   * An array representing the template areas for a grid layout. Each element in the array corresponds to a row in the grid, and each string within the row represents the template area
   * for the respective column. This property is used to define the structure of the grid layout in CSS grid-template-areas.
   * Documentation:
   * https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-areas
   * https://css-tricks.com/almanac/properties/g/grid-template-areas/
   * Example:
   * [
   *   ['header', 'header', 'nav'],
   *   ['main', 'main', 'aside'],
   *   ['footer', 'footer', 'footer']
   * ]
   *
   * In the example above, a 3x3 grid is defined with specific template areas for each cell, allowing for a flexible and named grid layout.
   *
   * @type {string[][]}
   * @default []
   */
  @property({ type: Array, reflect: true })
  templateAreas: string[][] = [];

  @property({ type: Array, reflect: true })
  widgets: LPWidgetConfig[] = [];

  private generateGridTemplate(): TemplateResult {
    const gridTemplate = repeat(
      this.widgets,
      (widget: LPWidgetConfig) => widget.id,
      (widget: LPWidgetConfig, index) => widget.isDynamic ? this.renderDynamicGridWidget(widget) : this.renderGridWidget(widget)
    );
    return html`${gridTemplate}`;
  }

  private renderGridWidget(widgetConfig: LPWidgetConfig): TemplateResult {
    const encodedTagName = htmlEncode(widgetConfig.content);
    const widgetStyles = {
      gridColumn: widgetConfig.gridColumnSpan && `${widgetConfig.gridColumnSpan}`,
      gridRow: widgetConfig.gridRowSpan && `${widgetConfig.gridRowSpan}`,
      gridArea: widgetConfig.gridArea && `${widgetConfig.gridArea}`,
    };

    const formattedProps = widgetConfig.props
      ? Object.entries(widgetConfig.props)
        .map(([key, value]) => {
            if (!value) return '';
            return typeof value === 'string' ?
              `${key}="${htmlEncode(value)}"` :
              `${key}="${htmlEncode(JSON.stringify(value))}"`
          }
        )
        .join(' ')
      : '';

    return html`<div class="grid-cell" style=${styleMap(widgetStyles)}>
      ${staticHtml`<${unsafeStatic(encodedTagName + ' ' + formattedProps)}/>`}
    </div>`;
  }

  private renderDynamicGridWidget(widgetConfig: LPWidgetConfig): TemplateResult {
    const widgetStyles = {
      gridColumn: widgetConfig.gridColumnSpan && `${widgetConfig.gridColumnSpan}`,
      gridRow: widgetConfig.gridRowSpan && `${widgetConfig.gridRowSpan}`,
      gridArea: widgetConfig.gridArea && `${widgetConfig.gridArea}`,
    };

    const widgetContentDecoded = htmlDecode(widgetConfig.dynamicContent ?? '')
      .replace(/\{company_name}/g, widgetConfig.props.companyName ?? '')
      .replace(/\{company_hash}/g, widgetConfig.props.companyHash ?? '');

    const companyType = widgetConfig.props.companyType ?? '';

    const template = html`
      <div class="grid-cell" style=${styleMap(widgetStyles)}>
        <div class="card--wrapper dynamic-widget">
          ${staticHtml`${unsafeStatic(widgetContentDecoded)}`}
        </div>
      </div>`;

    // catch click event on a href inside dynamic-widget and send to analytics
    setTimeout(() => {
      const dynamicWidget = this.shadowRoot?.querySelector('.dynamic-widget');
      if (dynamicWidget) {
        dynamicWidget.addEventListener('click', (event: Event) => {
          const target = event.target as HTMLElement;
          if (target.tagName === 'A') {
            analytics.trackPageInteraction({
              reference: `${getLandingPageReference(companyType)}` + ':dynamic-widget',
              asset: target.getAttribute('data-title') ?? '' as string,
            });
          }
        });
      }
    });

    return template;
  }

  private computeContainerStyles(): { gridTemplateAreas: string } {
    const gridTemplateAreas = this.templateAreas.map(row => `"${row.join(' ')}"`).join(' ');
    return {
      gridTemplateAreas: `${gridTemplateAreas}`,
    };
  }

  render() {
    return html`<div
      class="grid-container"
      id="gridContainer"
      style="${ifDefined(styleMap(this.computeContainerStyles()))}"
    >
      ${this.generateGridTemplate()}
    </div> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-landing-page-template': LandingPageTemplateWebComponent;
  }
}
